/**
 * src/components/ui/RadioGroup.jsx — a labelled group of radio options.
 *
 * `<fieldset>`/`<legend>`, per REDESIGN.md's Portal section ("group
 * questions with fieldset/legend; yes/no as large radio pairs, never
 * toggles"). Each option is a large tap target (the whole label, not just
 * the 16px input, is clickable and meets the portal's 44px minimum via
 * padding in ui.css) — built generically for any option list, not only
 * yes/no, since Settings-style single-choice pickers need the same pattern.
 */

/**
 * @param {{
 *   legend: string,
 *   name: string,
 *   value: string,
 *   onChange: (value: string) => void,
 *   options: { value: string, label: string }[],
 *   hint?: React.ReactNode,
 *   required?: boolean,
 * }} props
 * @returns {JSX.Element}
 */
export function RadioGroup({ legend, name, value, onChange, options, hint, required }) {
  const hintId = hint ? `${name}-hint` : undefined;
  return (
    <fieldset className="ui-fieldset" aria-describedby={hintId}>
      <legend>
        {legend}
        {required ? (
          <span className="ui-field-required" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </legend>
      {hint ? (
        <p id={hintId} className="ui-field-hint">
          {hint}
        </p>
      ) : null}
      <div className="ui-radio-group">
        {options.map((opt) => (
          <label key={opt.value} className="ui-radio-option">
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              required={required}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
